/**
 * Sections Service
 * Handles fetching and managing section data
 */

import { httpClient } from '../lib/httpClient';
import type { 
  SectionsResponse, 
  SectionsQueryParams, 
  SectionDetail,
  CreateSectionRequest,
  CreateSectionResponse,
  UpdateSectionRequest,
  UpdateSectionResponse
} from '../interfaces/apiInterfaces/sections';

/**
 * Fetch sections list with optional filters
 * @param params - Query parameters for filtering, pagination
 * @returns Sections list with pagination info
 */
export async function fetchSections(params?: SectionsQueryParams): Promise<SectionsResponse> {
  try {
    const response = await httpClient.get<SectionsResponse>(
      '/v1/sections',
      { params }
    );
    return response.data;
  } catch (error) {
    console.error('Error fetching sections:', error);
    throw error;
  }
}

/**
 * Fetch detailed section information by ID
 * @param sectionId - Section ID
 * @returns Detailed section information with questions
 */
export async function fetchSectionById(sectionId: string): Promise<SectionDetail> {
  try {
    const response = await httpClient.get<SectionDetail>(
      `/v1/sections/${sectionId}`
    );
    return response.data;
  } catch (error) {
    console.error(`Error fetching section details for ID ${sectionId}:`, error);
    throw error;
  }
}

/**
 * Create a new section
 * @param data - Section data to create
 * @returns Created section information
 */
export async function createSection(data: CreateSectionRequest): Promise<CreateSectionResponse> {
  try {
    const response = await httpClient.post<CreateSectionResponse>(
      '/v1/sections',
      data
    );
    return response.data;
  } catch (error) {
    console.error('Error creating section:', error);
    throw error;
  }
}

/**
 * Update an existing section
 * @param sectionId - Section ID to update (12-digit string)
 * @param data - Section data to update
 * @returns Updated section information
 */
export async function updateSection(sectionId: string, data: UpdateSectionRequest): Promise<UpdateSectionResponse> {
  try {
    const response = await httpClient.put<UpdateSectionResponse>(
      `/v1/sections/${sectionId}`,
      data
    );
    return response.data;
  } catch (error) {
    console.error(`Error updating section ${sectionId}:`, error);
    throw error;
  }
}

/**
 * Delete a section by ID
 * @param sectionId - Section ID to delete
 * @returns Promise that resolves when the section is deleted (204 No Content)
 */
export async function deleteSection(sectionId: string): Promise<void> {
  try {
    await httpClient.delete<void>(`/v1/sections/${sectionId}`);
  } catch (error) {
    console.error(`Error deleting section ${sectionId}:`, error);
    throw error;
  }
}
